import React, { useEffect } from 'react'
import { Grid, Box, ResponsiveContext } from 'grommet'
import { Outlet } from 'react-router-dom'
import GalarmEnterpriseSideBar from './GalarmEnterpriseSideBar'
import { Constants, I18n, colors } from 'galarm-config'
import {
  collection,
  collectionGroup,
  onSnapshot,
  orderBy,
  query,
  where
} from 'firebase/firestore'
import GlobalConfig from '../GlobalConfig'
import { useResize } from './useResize'

const EnterpriseAppLayout = () => {
  const enterpriseAccountId = window.localStorage.getItem('enterpriseAccountId')

  const size = React.useContext(ResponsiveContext)
  const windowSize = useResize()

  useEffect(() => {
    const currDate = Date.now()
    const newlyOpenedIncidentsQuery = query(
      collectionGroup(GlobalConfig.firestoreDb, 'incidents'),
      where('enterpriseAccountId', '==', enterpriseAccountId),
      where('timestamp', '>', currDate),
      orderBy('timestamp', 'desc')
    )

    const unsubscribe = onSnapshot(newlyOpenedIncidentsQuery, snapshot => {
      snapshot.docChanges().forEach(change => {
        if (change.type === 'added') {
          const incident = change.doc.data()
          GlobalConfig.showTransientAlert({
            message: I18n.t('newIncidentForAlert', {
              alertName: incident.alertName
            }),
            duration: Constants.AlertDurations.LONG
          })
        }
      })
    })

    const newTriggerFailuresQuery = query(
      collection(
        GlobalConfig.firestoreDb,
        'enterpriseAccounts',
        enterpriseAccountId,
        'alerts'
      ),
      where('lastTriggerFailure.timestamp', '>', currDate),
      orderBy('lastTriggerFailure.timestamp', 'desc')
    )

    // 'modified' changes are included since the same alert's trigger failure
    // document is updated (not recreated) on every subsequent failure.
    // However, Firestore also emits 'modified' for unrelated edits to an
    // alert that already matches this query, so we track the last notified
    // failure timestamp per document and only alert when it actually changes.
    const notifiedFailureTimestamps = new Map()
    const unsubscribeTriggerFailures = onSnapshot(
      newTriggerFailuresQuery,
      snapshot => {
        snapshot.docChanges().forEach(change => {
          if (change.type === 'added' || change.type === 'modified') {
            const alert = change.doc.data()
            const failureTimestamp = alert.lastTriggerFailure?.timestamp
            if (
              notifiedFailureTimestamps.get(change.doc.id) === failureTimestamp
            ) {
              return
            }
            notifiedFailureTimestamps.set(change.doc.id, failureTimestamp)
            GlobalConfig.showTransientAlert({
              message: I18n.t('triggerFailureForAlert', {
                alertName: alert.name,
                error: alert.lastTriggerFailure?.message
              }),
              type: 'danger',
              sticky: true
            })
          }
        })
      }
    )

    return () => {
      unsubscribe()
      unsubscribeTriggerFailures()
    }
  }, [enterpriseAccountId])

  const firstColumnWidths = {
    small: '75',
    medium: '100',
    large: '200',
    xlarge: '200'
  }
  const firstColumnWidth = firstColumnWidths[size]

  const mainWidth = windowSize.width - firstColumnWidth

  return (
    <Grid
      fill
      columns={[firstColumnWidth + 'px', mainWidth + 'px']}
      rows={['flex']}
      //[column,row] coordinates for start and end of areas
      areas={[
        { name: 'sidebar', start: [0, 0], end: [1, 0] },
        { name: 'main', start: [1, 0], end: [1, 0] }
      ]}>
      <Box gridArea="sidebar" background={colors.veryDarkBlueGrey}>
        <GalarmEnterpriseSideBar planType="enterprise" />
      </Box>
      <Box
        gridArea="main"
        background="lightTint"
        overflow={{ vertical: 'auto' }}>
        <Outlet />
      </Box>
    </Grid>
  )
}

export default EnterpriseAppLayout
